import { List, ListItemButton, ListItemText, Typography } from '@mui/material';
import { getRouteApi, Link, Outlet, useMatches } from '@tanstack/react-router';
import { MdChevronRight } from '@teamworksdev/icons';
import { useTranslation } from 'react-i18next';
import { Page } from '../../components/layout/page';
import { PageHeader } from '../../components/layout/page-header';
import { TeamSelect } from '../../components/layout/TeamSelect';
import { getLibraryTabSegment, useLibraryTabs } from './library-tabs';

const routeApi = getRouteApi('/orgAuth/orgWithNav/$teamId/library');

/**
 * Library area layout: header with team switcher and the sidebar nav of
 * permission-gated tab links. Tab gating lives in the route beforeLoad
 * (tabGuard, SC-4792), so by the time this wrapper mounts the active tab is
 * visible — there is no render-time redirect here.
 */
export function LibraryPageWrapper() {
  const { t } = useTranslation();
  const matches = useMatches();
  const { teamId } = routeApi.useParams();
  const links = useLibraryTabs();

  // Tab gating lives in the route beforeLoad (tabGuard, SC-4792), so by
  // the time this wrapper mounts the active tab is visible — there is no
  // render-time redirect. The wrapper renders the chrome and the visible
  // tab links.
  return (
    <Page>
      <PageHeader
        title={t('library')}
        rightSlot={<TeamSelect sx={{ mt: 0 }} teamId={teamId} />}
      />
      <div className="grow h-full overflow-hidden grid grid-cols-[248px_1fr]">
        <List component="nav" className="p-6">
          {links.map((link) => {
            const isActive = matches.some((m) =>
              m.pathname.includes(getLibraryTabSegment(link)),
            );
            const { label, to, ...linkProps } = link;
            return (
              <ListItemButton
                key={to}
                component={Link}
                to={to}
                {...linkProps}
                params={{ teamId }}
                search={{}}
                className="hover:bg-gray-200 rounded-md mb-2 px-3"
                selected={isActive}
              >
                <ListItemText>
                  <Typography
                    variant={isActive ? 'body1Semibold' : 'body1'}
                    className="text-sm min-h-6 flex items-center"
                  >
                    {label}
                  </Typography>
                </ListItemText>
                {isActive && <MdChevronRight size={25} />}
              </ListItemButton>
            );
          })}
        </List>
        <div className="grow h-full overflow-hidden py-6 pr-6">
          <Outlet />
        </div>
      </div>
    </Page>
  );
}
